import moment from 'moment';
import {
    clusterOverlappingGanttTasks,
    ganttBarAccent,
    isActiveTeamMember,
    stampGanttQueueOrder,
} from '../pages/kanbanBoard/ganttHelpers';

export const ganttPersonId = (userId) => `user:${Number(userId)}`;

/** Same id shape the library matches with `value.id === dependencies[j]`. */
export const ganttTaskId = (cardId, userId) =>
    `${Number(cardId)}:${Number(userId)}`;

const parseDate = (value) => {
    if (!value) return null;
    if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
    const m = moment(value);
    return m.isValid() ? m.toDate() : null;
};

const ensureEndAfterStart = (start, end, hours) => {
    if (end && end.getTime() > start.getTime()) return end;
    const h = Math.max(0.25, Number(hours) || 1);
    return new Date(start.getTime() + h * 3600 * 1000);
};

const clampProgress = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return 0;
    return Math.max(0, Math.min(100, n));
};

const isHighlighted = (task, highlightedCardIds) => {
    if (highlightedCardIds instanceof Set) return highlightedCardIds.has(Number(task.id));
    if (highlightedCardIds == null || highlightedCardIds === '') return false;
    return Number(task.id) === Number(highlightedCardIds);
};

const barStylesForTask = (task, { previewMap, highlightedCardIds } = {}) => {
    const spill = Number(task.bandwidth_spill_hours) > 0 || Boolean(task.missed_deadline);
    if (spill) {
        return {
            backgroundColor: '#fa5252',
            backgroundSelectedColor: '#e03131',
            progressColor: '#c92a2a',
            progressSelectedColor: '#a61e1e',
        };
    }
    if (previewMap?.has?.(task.id)) {
        return {
            backgroundColor: '#74c0fc',
            backgroundSelectedColor: '#4dabf7',
            progressColor: '#1c7ed6',
            progressSelectedColor: '#1864ab',
        };
    }
    if (isHighlighted(task, highlightedCardIds)) {
        return {
            backgroundColor: '#fcc419',
            backgroundSelectedColor: '#fab005',
            progressColor: '#f08c00',
            progressSelectedColor: '#e67700',
        };
    }
    const accent = ganttBarAccent(task);
    return {
        backgroundColor: accent.background,
        backgroundSelectedColor: accent.borderColor,
        progressColor: accent.borderColor,
        progressSelectedColor: accent.borderColor,
    };
};

/**
 * Convert one timeline card into a gantt-task-react Task.
 * Extra fields (sourceTask, sourceUserId) are for our handlers — not required by the library.
 */
export function mapCardToGanttTask(task, options = {}) {
    const userId = options.userId != null ? Number(options.userId) : Number(task.gantt_user_id) || 0;
    const projectId = options.projectId || (userId ? ganttPersonId(userId) : undefined);
    const start =
        parseDate(
            task.scheduled_start_at ||
                task.planned_start_at ||
                task.preferred_start_at ||
                task.window_start_at ||
                task.created_date
        ) || new Date();
    const rawEnd = parseDate(
        task.scheduled_end_at ||
            task.timeline_end_at ||
            options.previewMap?.get?.(task.id) ||
            task.deadline_at
    );
    const end = ensureEndAfterStart(
        start,
        rawEnd,
        task.allocated_hours ?? task.estimated_hours
    );

    const cardId = Number(task.id);
    const isMilestone =
        Boolean(task.is_milestone) ||
        (task.estimated_hours != null && Number(task.estimated_hours) === 0);

    return {
        id: ganttTaskId(cardId, userId),
        name: task.title || `Task ${task.id}`,
        type: isMilestone ? 'milestone' : 'task',
        start,
        end: isMilestone ? start : end,
        progress: clampProgress(task.progress),
        project: projectId,
        dependencies: [],
        styles: isMilestone
            ? {
                  backgroundColor: '#e9a825',
                  backgroundSelectedColor: '#d9480f',
                  progressColor: '#e9a825',
                  progressSelectedColor: '#d9480f',
              }
            : barStylesForTask(task, options),
        sourceTask: { ...task, is_milestone: isMilestone },
        sourceUserId: userId,
    };
}

/**
 * Library arrows for one person row: later groups wait on earlier groups.
 * Overlapping tasks share the same predecessors and merge into the next group.
 * If the last group is a merge with no successor, chain by priority so arrows remain.
 */
function chainMappedTasksByTimeline(sourceTasks, mapTask) {
    const groups = clusterOverlappingGanttTasks(sourceTasks).map((group) =>
        group.tasks.map((task) => mapTask(task))
    );
    const children = [];
    groups.forEach((group, index) => {
        const prevIds = index > 0 ? groups[index - 1].map((t) => t.id) : [];
        const hasNext = index < groups.length - 1;
        group.forEach((mapped, offset) => {
            if (!hasNext && offset > 0) {
                mapped.dependencies = [group[offset - 1].id];
            } else {
                mapped.dependencies = [...prevIds];
            }
            children.push(mapped);
        });
    });
    return children;
}

/**
 * People as `project` rows, their cards as child `task` rows.
 * Used by Project Timeline, My Timeline, User Timeline, and Team Planner.
 * Arrows are derived from the visible timeline (already filtered by that page),
 * not from a dependency field on the task form.
 */
export function mapMembersToGanttTasks(members = [], options = {}) {
    const { highlightedCardIds, previewMap } = options;
    const out = [];
    let order = 1;

    (members || []).forEach((member) => {
        const userId = Number(member.user?.user_id);
        if (!userId) return;
        const projectId = ganttPersonId(userId);
        const projectOrder = order;
        order += 1;
        const children = chainMappedTasksByTimeline(member.tasks || [], (task) =>
            mapCardToGanttTask(task, {
                userId,
                projectId,
                previewMap,
                highlightedCardIds,
            })
        );
        children.forEach((mapped) => {
            mapped.displayOrder = order;
            order += 1;
        });

        const starts = children.map((t) => t.start.getTime());
        const ends = children.map((t) => t.end.getTime());
        const start = starts.length ? new Date(Math.min(...starts)) : new Date();
        let end = ends.length ? new Date(Math.max(...ends)) : new Date(start.getTime() + 3600 * 1000);
        if (end.getTime() <= start.getTime()) {
            end = new Date(start.getTime() + 3600 * 1000);
        }
        const progress = children.length
            ? Math.round(children.reduce((sum, t) => sum + (Number(t.progress) || 0), 0) / children.length)
            : 0;

        const role = member.role || member.user?.user_type;
        const name = [
            `${member.user?.first_name || ''} ${member.user?.last_name || ''}`.trim() ||
                member.user?.email ||
                `User ${userId}`,
            role && role !== 'undefined' ? role : null,
        ]
            .filter(Boolean)
            .join(' · ');

        out.push({
            id: projectId,
            displayName: name,
            // Chart SVG prints `name` on the bar — keep it blank so the person
            // label only appears in the left list.
            name: '\u200B',
            type: 'project',
            start,
            end,
            progress,
            hideChildren: false,
            isDisabled: true,
            displayOrder: projectOrder,
            styles: {
                backgroundColor: 'transparent',
                backgroundSelectedColor: 'transparent',
                progressColor: 'transparent',
                progressSelectedColor: 'transparent',
            },
            sourceUser: member.user,
            sourceUserId: userId,
            sourceMember: member,
        });
        out.push(...children);
    });

    return out;
}

export function findSourceTask(ganttTask, members = []) {
    if (ganttTask?.sourceTask) return ganttTask.sourceTask;
    const rawId = String(ganttTask?.id || '');
    if (!rawId || rawId.startsWith('user:')) return null;
    const cardId = Number(rawId.split(':')[0]);
    const userId = Number(ganttTask?.sourceUserId ?? rawId.split(':')[1]);
    for (const member of members || []) {
        if (userId && Number(member.user?.user_id) !== userId) continue;
        const found = (member.tasks || []).find((t) => Number(t.id) === cardId);
        if (found) return found;
    }
    return null;
}

export function parsePersonUserId(ganttTask) {
    if (ganttTask?.sourceUserId != null) return Number(ganttTask.sourceUserId);
    const id = String(ganttTask?.id || '');
    if (id.startsWith('user:')) return Number(id.slice(5));
    const project = String(ganttTask?.project || '');
    if (project.startsWith('user:')) return Number(project.slice(5));
    return null;
}

/**
 * Group timeline cards by the assignee they were scheduled for.
 * Prefer `gantt_user_id` (one row per person-copy). Never fall back to
 * "first assignee" on a multi-assignee card — that was putting work on the
 * wrong person row.
 */
export function groupTasksByAssignee(tasks = []) {
    const byUser = new Map();
    const unassigned = [];

    const ensureRow = (user) => {
        const uid = Number(user?.user_id);
        if (!uid) return null;
        if (!byUser.has(uid)) {
            byUser.set(uid, { user, tasks: [] });
        } else if (user?.first_name && !byUser.get(uid).user?.first_name) {
            byUser.get(uid).user = { ...byUser.get(uid).user, ...user };
        }
        return byUser.get(uid);
    };

    (tasks || []).forEach((task) => {
        const focusId = task.gantt_user_id != null && task.gantt_user_id !== ''
            ? Number(task.gantt_user_id)
            : null;
        const list = task.assignees?.length
            ? task.assignees
            : task.assignee
              ? [task.assignee]
              : [];
        const active = list.filter((a) => a?.user_id && isActiveTeamMember(a));

        if (focusId) {
            const match = active.find((a) => Number(a.user_id) === focusId);
            const user = match || {
                user_id: focusId,
                first_name: task.assignee?.first_name || '',
                last_name: task.assignee?.last_name || '',
            };
            const row = ensureRow(user);
            if (row) row.tasks.push(task);
            else unassigned.push(task);
            return;
        }

        if (active.length === 1) {
            const row = ensureRow(active[0]);
            if (row) row.tasks.push(task);
            else unassigned.push(task);
            return;
        }

        unassigned.push(task);
    });

    const members = Array.from(byUser.values())
        .map((m) => ({ ...m, tasks: stampGanttQueueOrder(m.tasks) }))
        .sort((a, b) => {
            const an = `${a.user?.first_name || ''} ${a.user?.last_name || ''}`.trim();
            const bn = `${b.user?.first_name || ''} ${b.user?.last_name || ''}`.trim();
            return an.localeCompare(bn);
        });

    return { members, unassigned };
}

/** Move any mis-bucketed card onto the row matching `gantt_user_id`. */
export function regroupMembersByAssignee(members = []) {
    const tasks = [];
    const userById = new Map();
    (members || []).forEach((m) => {
        const uid = Number(m.user?.user_id);
        if (uid) userById.set(uid, m.user);
        (m.tasks || []).forEach((t) => tasks.push(t));
    });
    const { members: grouped, unassigned } = groupTasksByAssignee(tasks);
    grouped.forEach((m) => {
        const known = userById.get(Number(m.user?.user_id));
        if (known) m.user = { ...m.user, ...known };
    });
    return { members: grouped, unassigned };
}
